import api from 'services/api';
import { requestGetAccessToken } from 'services/authServices';
import { normalizeDocumentForPayload } from 'utils/document';

export type CustomerAddress = {
  siteId: string;
  addressType: string;
  addressLine?: string;
  addressNumber?: string;
  complement?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export type Customer = {
  corporateName: string;
  customerAddresses: CustomerAddress[];
};

type ApiError = {
  response?: {
    status?: number;
    data?:
      | { code?: string; message?: string }
      | Array<{ code?: string; message?: string }>;
  };
};

export const parseError = (
  error: unknown
): { code?: string; message?: string; status?: number } => {
  const { response } = (error ?? {}) as ApiError;
  const data = Array.isArray(response?.data)
    ? response?.data[0]
    : response?.data;

  return { code: data?.code, message: data?.message, status: response?.status };
};

const headers = async (): Promise<Record<string, string>> => ({
  'Content-Type': 'application/json',
  access_token: (await requestGetAccessToken()) as string,
});

const headersComClientId = async (): Promise<Record<string, string>> => ({
  ...(await headers()),
  client_id: process.env.REACT_APP_CLIENT_ID as string,
});

export const getCustomer = async (
  document: string,
  customerCode: string
): Promise<Customer | null> => {
  const documentParam = encodeURIComponent(
    normalizeDocumentForPayload(document)
  );
  const customerCodeParam = encodeURIComponent(customerCode);

  const response = await api.get(
    `industrial/customer/v1/customers?document=${documentParam}&code=${customerCodeParam}&type=EMP&_expand=CONTACT`,
    { headers: await headersComClientId() }
  );

  const [customer] = (response.data ?? []) as Customer[];

  return customer ?? null;
};

export const getShipToAddresses = (customer: Customer): CustomerAddress[] =>
  (customer.customerAddresses ?? []).filter(
    (address) => address.addressType === 'SHIP_TO'
  );

export const createUser = async (
  document: string,
  customerCode: string,
  siteId: string
): Promise<string> => {
  const response = await api.post(
    'industrial/customer/v1/users',
    {
      customerCode,
      document: normalizeDocumentForPayload(document),
      siteId,
      termsAccepted: true,
    },
    { headers: await headers() }
  );

  return (response.headers.location ?? '').split('/').pop() ?? '';
};

export const createContact = async (params: {
  userId: string;
  contact: string;
  contactType: 'EMAIL' | 'PHONE';
  document: string;
  customerCode: string;
  siteId: string;
  name: string;
  lastName: string;
}): Promise<string> => {
  const response = await api.post(
    `industrial/customer/v1/users/${params.userId}/contacts`,
    {
      name: params.name,
      lastName: params.lastName,
      contact: params.contact,
      contactType: params.contactType,
      customerCode: params.customerCode,
      document: normalizeDocumentForPayload(params.document),
      siteId: params.siteId,
    },
    { headers: await headers() }
  );

  return (response.headers.location ?? '').split('/').pop() ?? '';
};

export const createPassword = async (
  userId: string,
  contactId: string,
  password: string
): Promise<void> => {
  const response = await api.post(
    `industrial/customer/v1/users/${userId}/passwords`,
    { contactId, password, passwordConfirmation: password },
    { headers: await headers() }
  );

  const mfaHash = response.headers['x-mfa-hash'];

  if (mfaHash) {
    localStorage.setItem('@UltragazEmpresarial:X-Mfa-Hash', mfaHash);
  }
};

export const sendValidationCode = async (params: {
  userId: string;
  contactId: string;
  customerCode: string;
  document: string;
  email: string;
}): Promise<void> => {
  await api.post(
    `industrial/customer/v1/users/${params.userId}/tokens`,
    {
      tokenType: 'REGISTER',
      email: params.email,
      contactId: params.contactId,
      customerCode: params.customerCode,
      document: normalizeDocumentForPayload(params.document),
    },
    { headers: await headers() }
  );
};

export const validateRegistration = async (
  userId: string,
  token: string
): Promise<void> => {
  await api.post(
    `industrial/customer/v1/users/${userId}/validations`,
    { token },
    { headers: await headers() }
  );
};
